import { useState, useEffect } from 'react'
import { Card, Row, Col, Form, Button, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import { getMonthEndDateOnlyMx, getMonthStartDateOnlyMx, startOfLocalDayFromApi } from '../../utils/apiDateFormat'
import AccountDrilldownTable from './AccountDrilldownTable'

const BalanzaComprobacionReport = () => {
  const { clientId: clienteId, setClientId: setClienteId, selectDisabled } = useSyncedLocalClientId()
  const [startDate, setStartDate] = useState(getMonthStartDateOnlyMx())
  const [endDate, setEndDate] = useState(getMonthEndDateOnlyMx())
  const [exporting, setExporting] = useState(false)

  const handleExport = async (format) => {
    try {
      setExporting(true)
      const params = new URLSearchParams()
      if (clienteId) params.append('client_id', clienteId)
      if (startDate) params.append('fecha_inicio', startDate)
      if (endDate) params.append('fecha_fin', endDate)
      params.append('format', format)
      
      const response = await api.get(`/financial/reportes/exportar/balanza-comprobacion/?${params.toString()}`, {
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Balanza_Comprobacion.${format === 'excel' ? 'xlsx' : 'pdf'}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Error exportando:', error)
    } finally {
      setExporting(false)
    }
  }

  // Obtener lista de clientes para el filtro
  const { data: clientesData, isLoading: isLoadingClients } = useQuery(
    'clients',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    }
  )
  const clients = Array.isArray(clientesData) ? clientesData : []
  const selectedClient = clients.find(c => String(c.id) === String(clienteId))
  const selectedClientName = selectedClient
    ? (selectedClient.razon_social || selectedClient.business_name || `${selectedClient.nombre || ''} ${selectedClient.apellido_paterno || ''}`.trim())
    : ''

  // Obtener datos de la Balanza de Comprobación
  const { data: reportData, isLoading: isLoadingReport, error: reportError, refetch, dataUpdatedAt } = useQuery(
    ['balanza-comprobacion-report', clienteId, startDate, endDate],
    async () => {
      const params = new URLSearchParams()
      if (clienteId) params.append('client_id', clienteId)
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      const response = await api.get(`/financial/reportes/balanza-comprobacion/?${params.toString()}`)
      return response.data
    },
    { 
      enabled: !!clienteId && !!startDate && !!endDate, // Solo habilitar la consulta si todos los campos están presentes
      onError: (err) => {
        console.error("Error al cargar Balanza de Comprobación", err)
      }
    }
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validar fechas
    if (startDate && endDate) {
      const start = startOfLocalDayFromApi(startDate)
      const end = startOfLocalDayFromApi(endDate)
      if (start && end && start > end) {
        toast.error('La fecha de inicio debe ser anterior a la fecha de fin.')
        return
      }
    }
    
    if (!clienteId) {
      toast.error('Por favor, seleccione un cliente.')
      return
    }
    
    if (!startDate || !endDate) {
      toast.error('Por favor, seleccione ambas fechas.')
      return
    }
    
    refetch()
  }

  // Shortcuts de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Enter para generar reporte
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        const form = e.target.closest('form')
        if (form) {
          const submitButton = form.querySelector('button[type="submit"]')
          if (submitButton && !submitButton.disabled) {
            submitButton.click()
          }
        }
      }
      // Ctrl/Cmd + S para exportar a Excel
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (reportData && !exporting) {
          handleExport('excel')
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [reportData, exporting]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoadingClients) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando clientes...</p>
      </div>
    )
  }

  return (
    <div>
      <Card className="mb-4">
        <Card.Header as="h5">Balanza de Comprobación</Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col xs={12} md={4}>
                <Form.Group controlId="formClient">
                  <Form.Label>Cliente</Form.Label>
                  <Form.Select
                    value={clienteId}
                    onChange={(e) => setClienteId(e.target.value)}
                    disabled={selectDisabled}
                    required
                  >
                    <option value="">Seleccione un cliente</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.razon_social || client.business_name || `${client.nombre} ${client.apellido_paterno || ''}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group controlId="formStartDate">
                  <Form.Label>
                    Fecha Inicio
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Seleccione la fecha de inicio del período para la Balanza de Comprobación
                        </Tooltip>
                      }
                    >
                      <i className="bi bi-info-circle ms-2 text-info" style={{ cursor: 'help' }}></i>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                    max={endDate || undefined}
                  />
                  {startDate && endDate && new Date(startDate) > new Date(endDate) && (
                    <Form.Text className="text-danger">
                      La fecha de inicio debe ser anterior a la fecha de fin.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group controlId="formEndDate">
                  <Form.Label>
                    Fecha Fin
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Seleccione la fecha de fin del período para la Balanza de Comprobación
                        </Tooltip>
                      }
                    >
                      <i className="bi bi-info-circle ms-2 text-info" style={{ cursor: 'help' }}></i>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                    min={startDate || undefined}
                  />
                  {startDate && endDate && startOfLocalDayFromApi(startDate) > startOfLocalDayFromApi(endDate) && (
                    <Form.Text className="text-danger">
                      La fecha de fin debe ser posterior a la fecha de inicio.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} className="d-flex gap-2 flex-wrap">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Genera la Balanza de Comprobación con los filtros seleccionados (Ctrl+Enter)</Tooltip>}
                >
                  <Button variant="primary" type="submit" disabled={isLoadingReport || !clienteId || !startDate || !endDate} className="flex-grow-1 flex-md-grow-0">
                    {isLoadingReport ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-file-earmark-text me-2"></i>
                        Generar Reporte
                      </>
                    )}
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Exporta el reporte actual a formato PDF</Tooltip>}
                >
                  <span>
                    <Button variant="outline-danger" onClick={() => handleExport('pdf')} disabled={!reportData || exporting || isLoadingReport} className="flex-grow-1 flex-md-grow-0">
                      {exporting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Exportando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-file-earmark-pdf me-2"></i>
                          <span className="d-none d-md-inline">Exportar </span>PDF
                        </>
                      )}
                    </Button>
                  </span>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Exporta el reporte actual a formato Excel (Ctrl+S)</Tooltip>}
                >
                  <span>
                    <Button variant="outline-success" onClick={() => handleExport('excel')} disabled={!reportData || exporting || isLoadingReport} className="flex-grow-1 flex-md-grow-0">
                      {exporting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Exportando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-file-earmark-excel me-2"></i>
                          <span className="d-none d-md-inline">Exportar </span>Excel
                        </>
                      )}
                    </Button>
                  </span>
                </OverlayTrigger>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {reportError && (
        <Alert variant="danger" dismissible>
          <Alert.Heading>Error al cargar el reporte</Alert.Heading>
          <p className="mb-0">
            {reportError.response?.data?.detail || reportError.response?.data?.error || reportError.message || 'Ocurrió un error al generar la Balanza de Comprobación. Por favor, intente nuevamente.'}
          </p>
          {reportError.response?.status && (
            <small className="text-muted">Código de error: {reportError.response.status}</small>
          )}
        </Alert>
      )}

      {!reportData && !isLoadingReport && clienteId && startDate && endDate && (
        <Alert variant="info">
          <i className="bi bi-info-circle me-2"></i>
          Haga clic en &apos;Generar Reporte&apos; para ver la Balanza de Comprobación.
        </Alert>
      )}

      {isLoadingReport && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Generando Balanza de Comprobación...</p>
        </div>
      )}

      {reportData && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4">
            <div className="text-center mb-3">
              <h5 className="mb-1 fw-bold text-uppercase">{selectedClientName || 'Cliente'}</h5>
              <h6 className="mb-0 text-muted">
                Balanza de comprobación del {endDate || '—'}
              </h6>
            </div>
            <AccountDrilldownTable
              cuentas={reportData.cuentas || []}
              totales={reportData.totales}
              cuentasReportadas={reportData.cuentas_reportadas}
              clientId={clienteId}
              clientName={selectedClientName}
              startDate={startDate}
              endDate={endDate}
              reportCacheKey={dataUpdatedAt}
            />
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default BalanzaComprobacionReport
